/**
 * Mock API Service
 * Simulates API calls with realistic delays for demo mode
 * Provides the same interface as the real API service
 */

import { demoDataService } from './demo-data.service';
import type { DemoUser, DemoAsset } from './demo-data.service';
import type {
  Audit,
  AuditException,
  AuditActivityLog,
  AuditFilters,
  ExceptionFilters,
  PagedResult,
  DashboardMetrics,
} from '../../types/asset-audit';
import {
  AuditStatus,
  AuditType,
  AuditPriority,
  AuditScope,
  ExceptionStatus,
  ExceptionSeverity,
  ExceptionType,
} from '../../types/asset-audit';

/**
 * Simulate network delay
 */
const simulateDelay = (min: number = 300, max: number = 800): Promise<void> => {
  const delay = Math.floor(Math.random() * (max - min + 1)) + min;
  return new Promise(resolve => setTimeout(resolve, delay));
};

/**
 * Apply filters to audits
 */
const _filterAudits = (audits: Audit[], filters: AuditFilters): Audit[] => {
  let filtered = [...audits];

  if (filters.status && filters.status.length > 0) {
    filtered = filtered.filter(a => filters.status!.includes(a.status));
  }

  if (filters.type && filters.type.length > 0) {
    filtered = filtered.filter(a => filters.type!.includes(a.type));
  }

  if (filters.priority && filters.priority.length > 0) {
    filtered = filtered.filter(a => filters.priority!.includes(a.priority));
  }

  if (filters.assignedTo && typeof filters.assignedTo === 'string') {
    const assignedToId = filters.assignedTo;
    filtered = filtered.filter(a => 
      a.assignees?.some(assignee => assignee.userId === assignedToId)
    );
  }

  if (filters.startDateFrom) {
    filtered = filtered.filter(a => a.startDate && a.startDate >= filters.startDateFrom!);
  }

  if (filters.startDateTo) {
    filtered = filtered.filter(a => a.startDate && a.startDate <= filters.startDateTo!);
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(a =>
      a.auditNumber.toLowerCase().includes(searchLower) ||
      a.title.toLowerCase().includes(searchLower) ||
      a.description?.toLowerCase().includes(searchLower)
    );
  }

  return filtered;
};

/**
 * Apply filters to exceptions
 */
const _filterExceptions = (exceptions: AuditException[], filters: ExceptionFilters): AuditException[] => {
  let filtered = [...exceptions];

  if (filters.types && filters.types.length > 0) {
    filtered = filtered.filter(e => e.type && filters.types!.includes(e.type));
  }

  if (filters.severity && filters.severity.length > 0) {
    filtered = filtered.filter(e => filters.severity!.includes(e.severity));
  }

  if (filters.status && filters.status.length > 0) {
    filtered = filtered.filter(e => filters.status!.includes(e.status));
  }

  if (filters.priority && filters.priority.length > 0) {
    filtered = filtered.filter(e => filters.priority!.includes(e.priority));
  }

  if (filters.auditId) {
    filtered = filtered.filter(e => e.auditId === filters.auditId);
  }

  if (filters.reportedBy && typeof filters.reportedBy === 'string') {
    const reportedById = filters.reportedBy;
    filtered = filtered.filter(e => e.reportedBy === reportedById);
  }

  if (filters.reportedDateFrom) {
    filtered = filtered.filter(e => e.reportedDate >= filters.reportedDateFrom!);
  }

  if (filters.reportedDateTo) {
    filtered = filtered.filter(e => e.reportedDate <= filters.reportedDateTo!);
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(e =>
      e.exceptionNumber?.toLowerCase().includes(searchLower) ||
      e.title?.toLowerCase().includes(searchLower) ||
      e.description?.toLowerCase().includes(searchLower) ||
      e.assetCode.toLowerCase().includes(searchLower)
    );
  }

  return filtered;
};

/**
 * Paginate results
 */
const paginate = <T>(items: T[], page: number, pageSize: number): PagedResult<T> => {
  const totalCount = items.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const pagedItems = items.slice(startIndex, endIndex);

  return {
    items: pagedItems,
    data: pagedItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
};

/**
 * Sort items
 */
const sortItems = <T>(items: T[], sortBy?: string, sortOrder: 'asc' | 'desc' = 'asc'): T[] => {
  if (!sortBy) return items;

  return [...items].sort((a, b) => {
    const aValue = (a as any)[sortBy];
    const bValue = (b as any)[sortBy];

    if (aValue === bValue) return 0;

    let comparison = 0;
    if (aValue > bValue) comparison = 1;
    if (aValue < bValue) comparison = -1;

    return sortOrder === 'asc' ? comparison : -comparison;
  });
};

/**
 * Mock API Service
 */
export class MockApiService {
  // ==================== Dashboard ====================

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    await simulateDelay();

    const data = demoDataService.getData();
    // Include all active statuses to match Active Audits page
    const activeAudits = data.audits.filter(a => 
      a.status === AuditStatus.Draft ||
      a.status === AuditStatus.Assigned || 
      a.status === AuditStatus.InProgress || 
      a.status === AuditStatus.UnderReview ||
      a.status === AuditStatus.OnHold ||
      a.status === AuditStatus.Scheduled
    );
    const openExceptions = data.exceptions.filter(e => e.status !== ExceptionStatus.Resolved);
    const overdueAudits = activeAudits.filter(a => a.scheduledEndDate && new Date(a.scheduledEndDate) < new Date());

    // Calculate completion rate (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentAudits = data.audits.filter(a => 
      a.scheduledStartDate && new Date(a.scheduledStartDate) >= thirtyDaysAgo && 
      (a.status === AuditStatus.Completed || a.status === AuditStatus.Cancelled)
    );
    const recentCompleted = recentAudits.filter(a => a.status === AuditStatus.Completed);
    const completionRate = recentAudits.length > 0 
      ? (recentCompleted.length / recentAudits.length) * 100 
      : 85;

    return {
      activeAuditsCount: activeAudits.length,
      activeAuditsBreakdown: {
        draft: activeAudits.filter(a => a.status === AuditStatus.Draft).length,
        assigned: activeAudits.filter(a => a.status === AuditStatus.Assigned).length,
        inProgress: activeAudits.filter(a => a.status === AuditStatus.InProgress).length,
        underReview: activeAudits.filter(a => a.status === AuditStatus.UnderReview).length,
        onHold: activeAudits.filter(a => a.status === AuditStatus.OnHold).length,
        scheduled: activeAudits.filter(a => a.status === AuditStatus.Scheduled).length,
      },
      openExceptionsCount: openExceptions.length,
      openExceptionsBySeverity: {
        critical: openExceptions.filter(e => e.severity === ExceptionSeverity.Critical).length,
        high: openExceptions.filter(e => e.severity === ExceptionSeverity.High).length,
        medium: openExceptions.filter(e => e.severity === ExceptionSeverity.Medium).length,
        low: openExceptions.filter(e => e.severity === ExceptionSeverity.Low).length,
      },
      overdueAuditsCount: overdueAudits.length,
      overdueAudits: [...overdueAudits]
        .sort((a, b) => new Date(a.scheduledEndDate!).getTime() - new Date(b.scheduledEndDate!).getTime())
        .slice(0, 5)
        .map(a => ({
          auditNumber: a.auditNumber,
          title: a.title,
          daysOverdue: Math.floor((Date.now() - new Date(a.scheduledEndDate!).getTime()) / (1000 * 60 * 60 * 24)),
          assignee: (a.assignedUsers?.length ?? 0) > 0 ? a.assignedUsers![0].userName : 'Unassigned',
          priority: a.priority,
        })),
      completionRate: {
        percentage: Math.round(completionRate),
        trend: Math.floor(Math.random() * 10) - 5,
        sparklineData: Array.from({ length: 30 }, (_, i) => Math.floor(Math.random() * 10) + 1),
      },
      auditActivityTimeline: Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        return {
          date: date.toISOString().split('T')[0],
          started: Math.floor(Math.random() * 5),
          completed: Math.floor(Math.random() * 3),
          exceptions: Math.floor(Math.random() * 2),
        };
      }),
      exceptionDistribution: Object.values(ExceptionType).map(type => ({
        type,
        count: openExceptions.filter(e => e.type === type).length,
        percentage: openExceptions.length > 0 
          ? (openExceptions.filter(e => e.type === type).length / openExceptions.length) * 100 
          : 0,
      })),
    };
  }

  // ==================== Audits ====================

  async getAudits(params?: {
    statuses?: AuditStatus[];
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
    isOverdue?: boolean;
    priorities?: string[];
    types?: string[];
    assigneeIds?: string[];
  }): Promise<PagedResult<Audit>> {
    await simulateDelay();

    const data = demoDataService.getData();
    let audits = [...data.audits];

    // Apply filters
    if (params?.statuses && params.statuses.length > 0) {
      audits = audits.filter(a => params.statuses!.includes(a.status));
    }

    if (params?.search) {
      const searchLower = params.search.toLowerCase();
      audits = audits.filter(a => 
        a.title.toLowerCase().includes(searchLower) ||
        a.auditNumber.toLowerCase().includes(searchLower) ||
        a.description?.toLowerCase().includes(searchLower)
      );
    }

    if (params?.isOverdue) {
      audits = audits.filter(a => 
        a.scheduledEndDate && new Date(a.scheduledEndDate) < new Date()
      );
    }

    if (params?.priorities && params.priorities.length > 0) {
      audits = audits.filter(a => params.priorities!.includes(a.priority));
    }

    if (params?.types && params.types.length > 0) {
      audits = audits.filter(a => params.types!.includes(a.type));
    }

    if (params?.assigneeIds && params.assigneeIds.length > 0) {
      audits = audits.filter(a => 
        a.assignedUsers?.some(u => params.assigneeIds!.includes(u.userId))
      );
    }

    // Sort
    audits = sortItems(audits, params?.sortBy, params?.sortOrder ?? 'asc');

    // Paginate
    return paginate(audits, params?.page ?? 1, params?.pageSize ?? 20);
  }

  async getAuditById(id: string): Promise<Audit | null> {
    await simulateDelay();

    const data = demoDataService.getData();
    return data.audits.find(a => a.id === id) ?? null;
  }

  async createAudit(audit: Partial<Audit>): Promise<Audit> {
    await simulateDelay(500, 1200);

    const data = demoDataService.getData();
    const newAudit: Audit = {
      id: `audit-${Date.now()}`,
      auditNumber: `AUD-${String(data.audits.length + 1).padStart(6, '0')}`,
      title: audit.title ?? 'New Audit',
      description: audit.description,
      type: audit.type || AuditType.FullInventory,
      status: audit.status || AuditStatus.Scheduled,
      priority: audit.priority || AuditPriority.Medium,
      scope: audit.scope || AuditScope.AllAssets,
      scopeDetails: audit.scopeDetails ?? {},
      assignedUsers: audit.assignedUsers ?? [],
      scheduledStartDate: audit.scheduledStartDate ?? new Date().toISOString(),
      scheduledEndDate: audit.scheduledEndDate ?? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      createdBy: 'current-user',
      createdByName: 'Current User',
      createdDate: new Date().toISOString(),
      lastModifiedDate: new Date().toISOString(),
      completionPercentage: 0,
      completedAssets: 0,
      pendingAssets: audit.totalAssets ?? 0,
      totalAssets: audit.totalAssets ?? 0,
      exceptionCount: 0,
      isRecurring: audit.isRecurring ?? false,
      recurrencePattern: audit.recurrencePattern,
      isOverdue: false,
      allowPartialCompletion: true,
      requirePhotos: false,
      requireNotes: false,
      autoResolveExceptions: false,
    };

    data.audits.push(newAudit);
    demoDataService.updateData({ audits: data.audits });

    return newAudit;
  }

  async updateAudit(id: string, updates: Partial<Audit>): Promise<Audit> {
    await simulateDelay(400, 900);

    const data = demoDataService.getData();
    const index = data.audits.findIndex(a => a.id === id);

    if (index === -1) {
      throw new Error('Audit not found');
    }

    data.audits[index] = {
      ...data.audits[index],
      ...updates,
      lastModifiedDate: new Date().toISOString(),
    };

    demoDataService.updateData({ audits: data.audits });

    return data.audits[index];
  }

  async deleteAudit(id: string): Promise<void> {
    await simulateDelay(400, 800);

    const data = demoDataService.getData();
    data.audits = data.audits.filter(a => a.id !== id);
    demoDataService.updateData({ audits: data.audits });
  }

  // ==================== Audit Items (Asset Progress) ====================

  async getAuditItems(
    auditId: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<PagedResult<{
    id: string;
    auditId: string;
    assetId: string;
    assetName: string;
    assetCode: string;
    status: string;
    isVerified: boolean;
    isFlagged: boolean;
    isMissing: boolean;
    expectedLocation?: string;
    actualLocation?: string;
    locationVerified: boolean;
    locationChanged: boolean;
    condition?: string;
    conditionNotes?: string;
    conditionChanged: boolean;
    issues?: string[];
    notes?: string;
    photoUrls?: string[];
    photoCount: number;
    auditedBy?: string;
    auditedDate?: string;
    lastModifiedDate?: string;
  }>>{
    await simulateDelay();

    const data = demoDataService.getData();
    const audit = data.audits.find(a => a.id === auditId);
    
    if (!audit) {
      return paginate([], page, pageSize);
    }

    // Generate mock audit items based on the audit's scope
    // Use the actual asset IDs from scopeDetails if available
    let assetsToAudit: typeof data.assets;
    
    if (audit.scopeDetails?.assetIds && audit.scopeDetails.assetIds.length > 0) {
      // Filter assets to only include those in the audit's asset list
      assetsToAudit = data.assets.filter(asset => 
        audit.scopeDetails?.assetIds?.includes(asset.id)
      );
    } else if (audit.scopeDetails?.locationIds && audit.scopeDetails.locationIds.length > 0) {
      // Filter by location if specified (location is stored as string in DemoAsset)
      assetsToAudit = data.assets.filter(asset => 
        audit.scopeDetails?.locationIds?.some(locId => asset.location.includes(locId))
      );
    } else if (audit.scopeDetails?.departmentIds && audit.scopeDetails.departmentIds.length > 0) {
      // Filter by department if specified (department is stored as string in DemoAsset)
      assetsToAudit = data.assets.filter(asset => 
        audit.scopeDetails?.departmentIds?.some(deptId => asset.department.includes(deptId))
      );
    } else {
      // Use all assets or limit to totalAssets
      assetsToAudit = data.assets.slice(0, audit.totalAssets ?? 10);
    }

    // Helper function to generate deterministic pseudo-random values based on seed
    const seededRandom = (seed: string, index: number) => {
      const hash = seed.split('').reduce((acc, char, i) => acc + char.charCodeAt(0) * (i + 1), 0);
      return ((hash + index * 17) % 100) / 100;
    };

    // Condition values that match AssetAuditProgress type (lowercase)
    const conditionValues: Array<'good' | 'attention' | 'critical'> = ['good', 'attention', 'critical'];
    // Location status values
    const locationStatusValues: Array<'pending' | 'confirmed' | 'changed'> = ['pending', 'confirmed', 'changed'];
    
    // Sample notes for demo
    const sampleNotes = [
      'Asset verified and in good working condition. No issues found during inspection.',
      'Minor wear observed on exterior. Recommend scheduling maintenance within next quarter.',
      'Asset requires attention - some components showing signs of age. Review recommended.',
      'Location confirmed. Asset functioning as expected.',
      'Flagged for review - discrepancy noted between recorded and actual serial number.',
      undefined, // Some items have no notes
      undefined,
    ];

    const mockItems = assetsToAudit.map((asset, index) => {
      const seed = `${auditId}-${asset.id}`;
      const rand1 = seededRandom(seed, 1);
      const rand2 = seededRandom(seed, 2);
      const rand3 = seededRandom(seed, 3);
      const rand4 = seededRandom(seed, 4);
      const rand5 = seededRandom(seed, 5);
      
      // Determine status based on audit progress
      const isCompleted = index < (audit.completedAssets ?? 0);
      const isFlagged = !isCompleted && rand1 > 0.85; // ~15% of incomplete items are flagged
      const getStatus = (): 'in-progress' | 'completed' | 'flagged' => {
        if (isCompleted) return 'completed';
        if (isFlagged) return 'flagged';
        return 'in-progress';
      };
      const status = getStatus();
      
      // Determine condition (only for completed items, using lowercase values)
      const conditionIndex = Math.floor(rand2 * conditionValues.length);
      const condition = isCompleted ? conditionValues[conditionIndex] : undefined;
      
      // Determine location status
      const locationStatusIndex = Math.floor(rand3 * locationStatusValues.length);
      const locationStatus = locationStatusValues[locationStatusIndex];
      const locationChanged = locationStatus === 'changed';
      
      // Generate previous location if changed
      const locations = ['Warehouse A', 'Office Floor 1', 'Office Floor 2', 'Storage Room', 'Workshop', 'Main Building'];
      const previousLocation = locationChanged 
        ? locations[Math.floor(rand4 * locations.length)] 
        : undefined;
      
      // Generate photos (0-4 photos per item, deterministic)
      const photoCount = Math.floor(rand5 * 5);
      const photos = Array.from({ length: photoCount }, (_, photoIndex) => ({
        id: `photo-${asset.id}-${photoIndex}`,
        url: `https://picsum.photos/seed/${asset.id}-${photoIndex}/800/600`,
        thumbnailUrl: `https://picsum.photos/seed/${asset.id}-${photoIndex}/200/200`,
        caption: `Photo ${photoIndex + 1} of ${asset.name}`,
        uploadedAt: new Date(Date.now() - Math.floor(rand5 * 7) * 24 * 60 * 60 * 1000).toISOString(),
        uploadedBy: 'John Smith',
      }));
      
      // Generate notes (deterministic based on index)
      const notesIndex = Math.floor(rand1 * sampleNotes.length);
      const notes = sampleNotes[notesIndex];
      
      // Generate history entries
      const history = [
        {
          id: `history-${asset.id}-1`,
          timestamp: new Date(Date.now() - Math.floor(rand2 * 7) * 24 * 60 * 60 * 1000).toISOString(),
          action: 'created',
          description: 'Asset added to audit',
          user: 'System',
        },
      ];
      
      if (isCompleted) {
        history.push({
          id: `history-${asset.id}-2`,
          timestamp: new Date(Date.now() - Math.floor(rand3 * 3) * 24 * 60 * 60 * 1000).toISOString(),
          action: 'audited',
          description: 'Asset verified and audit completed',
          user: 'John Smith',
        });
      }
      
      if (isFlagged) {
        history.push({
          id: `history-${asset.id}-3`,
          timestamp: new Date(Date.now() - Math.floor(rand4 * 2) * 24 * 60 * 60 * 1000).toISOString(),
          action: 'flagged',
          description: 'Asset flagged for review: Requires additional verification',
          user: 'Jane Doe',
        });
      }

      return {
        id: `item-${auditId}-${asset.id}`,
        auditId,
        assetId: asset.id,
        assetName: asset.name,
        assetCode: asset.code,
        category: asset.category,
        status,
        isVerified: isCompleted,
        isFlagged,
        isMissing: false,
        expectedLocation: asset.location,
        actualLocation: asset.location,
        currentLocation: asset.location,
        previousLocation,
        locationVerified: isCompleted,
        locationChanged,
        locationStatus,
        condition,
        conditionNotes: condition === 'attention' || condition === 'critical' 
          ? 'Requires maintenance review' 
          : undefined,
        conditionChanged: false,
        issues: isFlagged ? ['Requires verification'] : undefined,
        notes,
        photos,
        photoUrls: photos.map(p => p.url),
        photoCount: photos.length,
        history,
        flaggedForReview: isFlagged,
        lastUpdated: new Date(Date.now() - Math.floor(rand1 * 7) * 24 * 60 * 60 * 1000).toISOString(),
        auditedBy: isCompleted ? 'John Smith' : undefined,
        auditedDate: isCompleted ? new Date().toISOString() : undefined,
        lastModifiedDate: new Date().toISOString(),
      };
    });

    return paginate(mockItems, page, pageSize);
  }

  // ==================== Exceptions ====================

  async getExceptions(params?: {
    pageSize?: number;
    page?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    statuses?: string[];
    severities?: string[];
    types?: string[];
    auditIds?: string[];
  }): Promise<PagedResult<AuditException>> {
    await simulateDelay();

    const data = demoDataService.getData();
    let exceptions = [...data.exceptions];

    // Apply filters
    if (params?.statuses && params.statuses.length > 0) {
      exceptions = exceptions.filter(e => params.statuses!.includes(e.status));
    }

    if (params?.severities && params.severities.length > 0) {
      exceptions = exceptions.filter(e => params.severities!.includes(e.severity));
    }

    if (params?.types && params.types.length > 0) {
      exceptions = exceptions.filter(e => e.type && params.types!.includes(e.type));
    }

    if (params?.auditIds && params.auditIds.length > 0) {
      exceptions = exceptions.filter(e => params.auditIds!.includes(e.auditId));
    }

    // Sort
    exceptions = sortItems(exceptions, params?.sortBy, params?.sortOrder ?? 'asc');

    // Paginate
    return paginate(exceptions, params?.page ?? 1, params?.pageSize ?? 20);
  }

  async getExceptionById(id: string): Promise<AuditException | null> {
    await simulateDelay();

    const data = demoDataService.getData();
    return data.exceptions.find(e => e.id === id) ?? null;
  }

  async resolveException(id: string, resolution: { notes: string; resolvedBy: string }): Promise<AuditException> {
    await simulateDelay(500, 1000);

    const data = demoDataService.getData();
    const index = data.exceptions.findIndex(e => e.id === id);

    if (index === -1) {
      throw new Error('Exception not found');
    }

    data.exceptions[index] = {
      ...data.exceptions[index],
      status: ExceptionStatus.Resolved,
      resolvedDate: new Date().toISOString(),
      resolvedBy: resolution.resolvedBy,
      resolvedByName: 'Current User',
      resolutionNotes: resolution.notes,
    };

    demoDataService.updateData({ exceptions: data.exceptions });

    return data.exceptions[index];
  }

  // ==================== Activity Logs ====================

  async getActivityLogs(
    auditId: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<PagedResult<AuditActivityLog>> {
    await simulateDelay();

    const data = demoDataService.getData();
    const logs = data.activityLogs
      .filter(l => l.auditId === auditId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return paginate(logs, page, pageSize);
  }

  // ==================== Users ====================

  async getUsers(): Promise<DemoUser[]> {
    await simulateDelay();

    const data = demoDataService.getData();
    return data.users;
  }

  async getUserById(id: string): Promise<DemoUser | null> {
    await simulateDelay();

    const data = demoDataService.getData();
    return data.users.find(u => u.id === id) ?? null;
  }

  // ==================== Assets ====================

  async getAssets(
    page: number = 1,
    pageSize: number = 50,
    search?: string
  ): Promise<PagedResult<DemoAsset>> {
    await simulateDelay();

    const data = demoDataService.getData();
    let assets = data.assets;

    if (search) {
      const searchLower = search.toLowerCase();
      assets = assets.filter(a =>
        a.code.toLowerCase().includes(searchLower) ||
        a.name.toLowerCase().includes(searchLower) ||
        a.category.toLowerCase().includes(searchLower)
      );
    }

    return paginate(assets, page, pageSize);
  }

  async getAssetById(id: string): Promise<DemoAsset | null> {
    await simulateDelay();

    const data = demoDataService.getData();
    return data.assets.find(a => a.id === id) ?? null;
  }
}

// Export singleton instance
export const mockApiService = new MockApiService();
